import { useQueries } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'

import Reveal from '@/components/motion/Reveal'
import Stagger from '@/components/motion/Stagger'
import SkeletonGroup from '@/components/motion/SkeletonGroup'
import EmptyState from '@/components/ui/EmptyState'
import SegmentedControl, { type SegmentOption } from '@/components/ui/SegmentedControl'
import Skeleton from '@/components/ui/Skeleton'
import { planQueryOptions, usePlans, useSubscriptionMe } from '@/features/subscriptions/api/queries'
import {
  buildCatalog,
  defaultPeriod,
  offerFor,
  periodLabel,
  periodPhrase,
  monthsIn,
  savingOverMonthly,
  type Catalog,
} from '@/features/subscriptions/lib/catalog'
import { planSummary } from '@/features/subscriptions/lib/features'
import { PLANS } from '@/features/onboarding/plans'
import { useOnboardingStore } from '@/features/onboarding/store'
import { div, mul } from '@/lib/money'
import { formatNPR } from '@/lib/format'
import { requireAuth } from '@/lib/auth/guards'
import PlanCard from '@/screens/onboarding/components/PlanCard'
import StepProgress from '@/screens/onboarding/components/StepProgress'
import type { KnownTier, Plan } from '@/schemas/subscriptions'

export const Route = createFileRoute('/onboarding/plan')({
  beforeLoad: requireAuth,
  component: PickPlanScreen,
})

/**
 * The toggle's own "−N%" hint (Figma's "Yearly −30%"): computed from real
 * prices only, never the Figma figure. Tries Sadasya's base offer first, then
 * Guru's, since the hint sits on one shared control above three cards that
 * may each discount a period differently — this is the best single number to
 * show there, not a per-card claim. `null` when neither tier sells BOTH the
 * monthly and this period (dev: Sadasya has no yearly), which draws no hint
 * rather than a stale or invented one.
 */
function periodDiscountPercent(catalog: Catalog, periodDays: number): number | null {
  if (periodDays === 30) return null
  for (const tier of ['bull', 'shark'] as const) {
    const monthly = offerFor(catalog, tier, 30)?.base ?? null
    const offer = offerFor(catalog, tier, periodDays)?.base ?? null
    if (monthly === null || offer === null) continue
    const months = monthsIn(offer.periodDays)
    const saving = savingOverMonthly(offer, monthly)
    if (months === null || saving === null) continue
    // Both stay Money so the arithmetic stays in decimal space; only the
    // already-tiny ratio is converted to a number, for `Math.floor`.
    const fullPrice = mul(monthly.price.total, months)
    const percent = Math.floor(Number(div(saving, fullPrice)) * 100)
    if (percent > 0) return percent
  }
  return null
}

/** This tier's base (non-signal) offer for the active period, or null if it sells none. */
const baseOffer = (catalog: Catalog, tier: KnownTier, periodDays: number): Plan | null =>
  offerFor(catalog, tier, periodDays)?.base ?? null

function PickPlanSkeleton() {
  return (
    <SkeletonGroup className="mt-10 grid gap-6 pb-16 md:grid-cols-3">
      {PLANS.map((plan) => (
        <div key={plan.tier} className="rounded-lg border border-border-subtle bg-card p-5">
          <Skeleton className="size-14 rounded-full" />
          <Skeleton className="mt-4 h-7 w-24" />
          <Skeleton className="mt-2 h-5 w-32" />
          <hr className="border-border-subtle my-4" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <Skeleton className="mt-6 h-13 w-full" />
        </div>
      ))}
    </SkeletonGroup>
  )
}

function PickPlanScreen() {
  const navigate = useNavigate()
  const choice = useOnboardingStore((s) => s.choice)
  const setChoice = useOnboardingStore((s) => s.setChoice)
  const [period, setPeriod] = useState<number>()

  // Free is never in `plans/` (CONTRACT §6) — its real id is only reachable
  // through the signed-in member's own entitlement, which a fresh signup
  // already holds (registration grants a live Free row). Onboarding runs
  // behind `requireAuth`, so this is always available, never hardcoded.
  const meQuery = useSubscriptionMe()
  const plansQuery = usePlans()

  const catalog = plansQuery.data === undefined ? null : buildCatalog(plansQuery.data)
  const periods = catalog?.periods ?? []
  const activePeriod =
    period !== undefined && periods.includes(period)
      ? period
      : catalog === null
        ? undefined
        : (defaultPeriod(catalog) ?? undefined)

  const freeId = meQuery.data?.planId ?? null
  const bullOffer =
    catalog !== null && activePeriod !== undefined ? baseOffer(catalog, 'bull', activePeriod) : null
  const sharkOffer =
    catalog !== null && activePeriod !== undefined
      ? baseOffer(catalog, 'shark', activePeriod)
      : null

  const detailIds = [freeId, bullOffer?.id ?? null, sharkOffer?.id ?? null].filter(
    (id): id is number => id !== null,
  )
  const details = useQueries({ queries: detailIds.map((id) => planQueryOptions(id)) })
  const detailById = new Map(detailIds.map((id, index) => [id, details[index]]))

  const loading = plansQuery.isPending || meQuery.isPending
  const failed = plansQuery.isError || meQuery.isError
  const retry = () => {
    void plansQuery.refetch()
    void meQuery.refetch()
  }

  const activeTier = choice?.tier ?? 'free'

  // Picking a card only selects it (owner U8, radio semantics); the button on it continues.
  const select = (tier: KnownTier, planId: number) => {
    setChoice({ tier, planId })
  }
  const choose = (tier: KnownTier, planId: number) => {
    select(tier, planId)
    void navigate({ to: '/onboarding/done' })
  }

  const periodOptions: readonly SegmentOption<string>[] =
    catalog === null
      ? []
      : periods.map((days) => {
          const hint = periodDiscountPercent(catalog, days)
          return {
            value: String(days),
            label: hint === null ? periodLabel(days) : `${periodLabel(days)} −${String(hint)}%`,
          }
        })

  return (
    <Reveal variant="fade-in" className="bg-background text-foreground min-h-dvh px-8 py-8">
      <Stagger className="relative mx-auto max-w-275">
        <Link
          to="/onboarding/persona"
          aria-label="Back"
          // rest:ub — the persona step's back chevron: grey to white, 2px left on hover.
          className="text-muted-foreground hover:text-foreground focus-visible:text-foreground absolute top-0 left-0 transition-[color,translate] duration-fast before:absolute before:-inset-3 before:content-[''] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control motion-safe:hover:-translate-x-(--motion-nudge) lg:before:hidden"
        >
          <ChevronLeft className="size-6" aria-hidden />
        </Link>

        <StepProgress current={2} />

        <h1 className="mt-10 text-center text-display-l">Choose your plan</h1>

        {periodOptions.length > 1 && activePeriod !== undefined && (
          <div className="mt-6 flex justify-center">
            <SegmentedControl
              options={periodOptions}
              value={String(activePeriod)}
              onChange={(value) => {
                setPeriod(Number(value))
              }}
              ariaLabel="Billing period"
            />
          </div>
        )}

        {failed ? (
          <EmptyState
            kind="failed"
            size="page"
            title="Plans did not load"
            description="Check your connection and try again."
            onRetry={retry}
            retrying={plansQuery.isFetching || meQuery.isFetching}
          />
        ) : loading ? (
          <PickPlanSkeleton />
        ) : (
          <Stagger className="mt-10 grid animate-none gap-6 pb-16 md:grid-cols-3">
            {PLANS.map((presentation) => {
              const tier = presentation.tier
              const isFree = tier === 'free'
              const offer = tier === 'bull' ? bullOffer : tier === 'shark' ? sharkOffer : null
              const planId = isFree ? freeId : (offer?.id ?? null)
              const detail = planId === null ? undefined : detailById.get(planId)?.data
              // Reached only once the catalogue has loaded (the `loading`
              // branch above covers the page while it has not), so a null
              // offer here means this tier genuinely sells nothing for the
              // active period (Sadasya has no yearly on dev) — never a stale
              // price from the last period or an invented one.
              const unavailable = !isFree && offer === null

              // The CTA slot already says "Not sold for this period." below —
              // no second line repeating it here.
              const priceLine = isFree
                ? 'Free'
                : offer === null
                  ? ''
                  : `${formatNPR(offer.price.total, { whole: offer.price.total.endsWith('.00') })} ${periodPhrase(offer.periodDays)}`

              return (
                <PlanCard
                  key={tier}
                  plan={presentation}
                  priceLine={priceLine}
                  unavailable={unavailable}
                  tagline={
                    detail === undefined
                      ? undefined
                      : detail === null
                        ? null
                        : planSummary(detail.features)
                  }
                  features={detail?.features}
                  planId={planId}
                  selected={activeTier === tier}
                  onSelect={(id) => {
                    select(tier, id)
                  }}
                  onChoose={(id) => {
                    choose(tier, id)
                  }}
                />
              )
            })}
          </Stagger>
        )}
      </Stagger>
    </Reveal>
  )
}
